// Shared types for Unified Schema View, Chat, and Data Preview

// ── Column & Table Summaries (canonical, extracted from SemanticAnalysisPanel) ──

export interface ColumnSummary {
  /** Canonical DuckDB-sanitized column name (snake_case). Used for lookups,
   *  SQL references, and mapping identifiers. */
  name: string;
  /** Raw source header before sanitization (e.g. "TRIAL CODE"). Present on
   *  post-canonicalization profiles; falls back to ``name`` when absent. */
  original_name?: string;
  semantic_role: string;
  description: string;
  pii_likelihood: number;
  is_primary_key?: boolean;
  is_foreign_key?: boolean;
  fk_references?: string;
  data_type?: string;
}

export interface JoinDefinition {
  on: string[];
  to: string;
  join_type?: string;
  confidence?: number;
  description?: string;
}

export interface TableSummary {
  file_name: string;
  /** Original source identifier preserved from profiling — may be a CSV file name,
   *  Excel sheet, database table, etc. Used for matching against profile data and
   *  requesting source previews. Set by the semantic summary pipeline as `file_origin`. */
  file_origin?: string;
  semantic_name: string;
  description: string;
  entity_type: string;
  primary_key_candidates?: string[];
  foreign_key_candidates?: string[];
  likely_joins?: JoinDefinition[];
  column_summaries: ColumnSummary[];
}

// ── Unified merged data (stats + semantic) ──

export interface UnifiedColumnData {
  /** Canonical DuckDB-sanitized column name. */
  name: string;
  /** Raw source header for human display. Falls back to ``name`` when absent. */
  original_name?: string;
  // Statistical
  data_type: string;
  null_percentage: number;
  unique_count: number;
  sample_values: string[];
  min_value?: string | number;
  max_value?: string | number;
  avg_length?: number;
  // Semantic
  semantic_role: string;
  description: string;
  pii_likelihood: number;
  is_primary_key?: boolean;
  is_foreign_key?: boolean;
  fk_references?: string;
}

export interface UnifiedTableData {
  file_name: string;
  /** Original source identifier (file name, sheet name, DB table name, etc.)
   *  preserved from profiling. Used for data preview requests and traceability. */
  file_origin?: string;
  semantic_name: string;
  description: string;
  entity_type: string;
  // Statistical
  row_count: number;
  file_size_mb: number;
  // Keys & joins
  primary_key_candidates: string[];
  foreign_key_candidates: string[];
  likely_joins: JoinDefinition[];
  // Merged columns
  columns: UnifiedColumnData[];
}

// ── Chat types ──

export type ChatSuggestionType =
  | 'set_pk'
  | 'add_relationship'
  | 'update_column'
  | 'set_entity_type';

export type ChatSuggestionStatus = 'pending' | 'accepted' | 'rejected';

export interface ChatSuggestion {
  id: string;
  type: ChatSuggestionType;
  description: string;
  payload: Record<string, any>;
  status: ChatSuggestionStatus;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: ChatSuggestion[];
  isLoading?: boolean;
  error?: boolean;
}

// ── Data Preview types ──

export interface DataPreviewResponse {
  file_name: string;
  columns: string[];
  rows: any[][];
  row_count: number;
  total_rows: number;
}

// ── Constants (extracted from SemanticAnalysisPanel) ──

export const SEMANTIC_ROLES = [
  'identifier',
  'foreign_key',
  'measurement',
  'category',
  'date',
  'datetime',
  'timestamp',
  'text',
  'description',
  'name',
  'code',
  'status',
  'flag',
  'amount',
  'count',
  'percentage',
  'location',
  'technical',
  'unknown',
];

export const ENTITY_TYPE_SUGGESTIONS = [
  // Healthcare/Clinical
  'patient',
  'subject',
  'visit',
  'encounter',
  'lab_result',
  'medication',
  'procedure',
  'diagnosis',
  'adverse_event',
  // Generic business
  'customer',
  'order',
  'transaction',
  'product',
  'invoice',
  'account',
  'employee',
  // Research
  'study',
  'protocol',
  'observation',
  'specimen',
  // Meta
  'reference',
  'configuration',
  'unknown',
];